
import React from 'react';
import { formatExitDate } from './ReviewCard';
import { BadgeCheck, BadgeX } from 'lucide-react';
import { Review } from '@/lib/types';

interface ReviewDetailsProps {
  jobTitle?: string;
  location: string;
  salary?: number; // Add salary prop
}

interface ReviewMetadataProps {
  review: Review;
  hasJobTitle: boolean;
}

// Helper function to format salary with a thousands separator
const formatSalary = (salary: number | undefined) => {
  if (!salary) return '';
  
  return new Intl.NumberFormat('en-AU', { 
    style: 'currency',
    currency: 'AUD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(salary) + ' + Super';
};

// Individual components for review metadata
const ReviewMetadata = ({ review, hasJobTitle }: ReviewMetadataProps) => {
  return (
    <div className="mb-4">
      {hasJobTitle && (
        <div className="flex items-center flex-wrap gap-x-2 mb-2">
          <h3 className="font-semibold text-redflags-dark">{review.jobTitle}</h3>
          <span className="text-redflags-gray">{review.location}</span>
        </div>
      )}
      
      {/* Add salary display */}
      {review.salary && (
        <div className="text-sm text-redflags-gray mb-2">
          {formatSalary(review.salary)}
        </div>
      )}
      
      <div className="flex flex-wrap gap-2 mt-1">
        {review.exitDate && (
          <span className="text-xs bg-redflags-light/50 px-2 py-1 rounded-md">
            Left {formatExitDate(review.exitDate)}
          </span>
        )}
        
        {review.tenure && (
          <span className="text-xs bg-redflags-light/50 px-2 py-1 rounded-md">
            {review.tenure}
          </span>
        )}
        
        {review.fairWorkInvolved === true && (
          <span className="text-xs bg-red-100 text-red-700 px-2 py-1 rounded-md">
            Fair Work involved
          </span>
        )}
      </div>
    </div>
  );
};

// Details component without extra metadata
const ReviewDetails = ({ jobTitle, location, salary }: ReviewDetailsProps) => {
  return (
    <div className="mb-3">
      {jobTitle && (
        <div className="flex items-center flex-wrap gap-x-2 mb-1">
          <h3 className="font-semibold text-redflags-dark">{jobTitle}</h3>
          <span className="text-redflags-gray">{location}</span>
        </div>
      )}
      
      {/* Add salary display */}
      {salary && (
        <div className="text-sm text-redflags-gray">
          {formatSalary(salary)}
        </div>
      )}
    </div>
  );
};

// Experience indicator component
const ExperienceIndicator = ({ 
  positiveExperience 
}: { 
  positiveExperience?: boolean 
}) => {
  if (positiveExperience === undefined) return null;
  
  return positiveExperience ? (
    <div className="flex items-center gap-1 text-green-600">
      <BadgeCheck className="w-5 h-5" />
      <span className="text-base font-semibold">Positive</span>
    </div>
  ) : (
    <div className="flex items-center gap-1 text-red-600">
      <BadgeX className="w-5 h-5" />
      <span className="text-base font-semibold">Negative</span>
    </div>
  );
};

// Attach components as properties to ReviewMetadata
ReviewMetadata.ExperienceIndicator = ExperienceIndicator;

export default ReviewMetadata;
